import type { TaxDeductionReview } from "./hooks/useTaxDeductions";
import type { ApiPaymentRecord } from "./hooks/usePaymentRecords";

export const MEDICAL_DEDUCTION_TAX_YEAR = 2025;
export const MEDICAL_DEDUCTION_AGI_RATE = 0.075;

export type MedicalExpenseReviewStatus =
  "needs-review" | "included" | "excluded";

export type MedicalExpenseCandidate = {
  id: string;
  label: string;
  provider: string;
  occurredOn: string;
  grossAmount: number;
  patientPaidAmount: number;
  nonPatientAllocatedAmount: number;
  reviewStatus: MedicalExpenseReviewStatus;
};

export type MedicalExpensePaymentBreakdown = {
  grossAmount: number;
  patientPaidAmount: number;
  nonPatientAllocatedAmount: number;
  hasAllocations: boolean;
};

/**
 * The deliberately narrow client-side result used by the evidence-review UI.
 * This is not a tax-savings or net-cost result; those require the API-backed
 * contract documented in docs/features/TAX_DEDUCTION_CALCULATION_CONTRACT.md.
 */
export type MedicalExpenseDeductionCalculation = {
  grossExpenseTotal: number;
  potentialExpenseTotal: number;
  confirmedExpenseTotal: number;
  potentialNonPatientAllocatedTotal: number;
  confirmedNonPatientAllocatedTotal: number;
  threshold: number | null;
  potentialDeduction: number | null;
  confirmedDeduction: number | null;
};

export function deriveMedicalExpensePaymentBreakdown(
  record: ApiPaymentRecord,
): MedicalExpensePaymentBreakdown {
  const lineItems = record.lineItems;
  const grossAmount = Math.max(record.billedAmount ?? record.amount ?? 0, 0);
  if (!lineItems?.length) {
    return {
      grossAmount,
      patientPaidAmount: Math.max(record.amount ?? 0, 0),
      nonPatientAllocatedAmount: 0,
      hasAllocations: false,
    };
  }

  const hasAllocations = lineItems.some(
    (lineItem) => lineItem.allocations.length > 0,
  );
  if (!hasAllocations) {
    return {
      grossAmount,
      patientPaidAmount: Math.max(record.amount ?? 0, 0),
      nonPatientAllocatedAmount: 0,
      hasAllocations: false,
    };
  }

  return lineItems.reduce<MedicalExpensePaymentBreakdown>(
    (breakdown, lineItem) => {
      for (const allocation of lineItem.allocations) {
        if (allocation.kind === "patient") {
          breakdown.patientPaidAmount += Math.max(allocation.amount, 0);
        } else {
          breakdown.nonPatientAllocatedAmount += Math.max(allocation.amount, 0);
        }
      }
      return breakdown;
    },
    {
      grossAmount,
      patientPaidAmount: 0,
      nonPatientAllocatedAmount: 0,
      hasAllocations: true,
    },
  );
}

export function deriveMedicalExpenseCandidates(
  records: ApiPaymentRecord[],
  reviews: TaxDeductionReview[],
  taxYear: number,
): MedicalExpenseCandidate[] {
  const reviewByRecordId = new Map(
    reviews.map((review) => [review.paymentRecordId, review]),
  );

  return records
    .filter(
      (record) =>
        (record.kind === "payment" || record.kind === "receipt") &&
        record.occurredOn?.startsWith(`${taxYear}-`),
    )
    .map((record) => {
      const review = reviewByRecordId.get(record.id);
      const paymentBreakdown = deriveMedicalExpensePaymentBreakdown(record);
      const reviewStatus: MedicalExpenseReviewStatus =
        review?.decision === "include"
          ? "included"
          : review?.decision === "exclude"
            ? "excluded"
            : "needs-review";

      return {
        id: record.id,
        label: record.label?.trim() || "Medical expense",
        provider: record.provider?.trim() || "Provider not set",
        occurredOn: record.occurredOn ?? "",
        grossAmount: paymentBreakdown.grossAmount,
        patientPaidAmount: paymentBreakdown.patientPaidAmount,
        nonPatientAllocatedAmount: paymentBreakdown.nonPatientAllocatedAmount,
        reviewStatus,
      };
    })
    .filter((candidate) => candidate.patientPaidAmount > 0)
    .sort((left, right) => right.occurredOn.localeCompare(left.occurredOn));
}

export function calculateMedicalExpenseDeduction(
  candidates: MedicalExpenseCandidate[],
  adjustedGrossIncome: number | null,
  itemizesDeductions: boolean | null | undefined = null,
): MedicalExpenseDeductionCalculation {
  const potentialCandidates = candidates.filter(
    (candidate) => candidate.reviewStatus !== "excluded",
  );
  const confirmedCandidates = candidates.filter(
    (candidate) => candidate.reviewStatus === "included",
  );
  const potentialExpenseTotal = potentialCandidates.reduce(
    (total, candidate) => total + candidate.patientPaidAmount,
    0,
  );
  const confirmedExpenseTotal = confirmedCandidates.reduce(
    (total, candidate) => total + candidate.patientPaidAmount,
    0,
  );
  const threshold =
    adjustedGrossIncome == null
      ? null
      : adjustedGrossIncome * MEDICAL_DEDUCTION_AGI_RATE;
  const itemizationKnown = itemizesDeductions != null;
  const deductionEligible = itemizesDeductions === true;

  return {
    grossExpenseTotal: potentialCandidates.reduce(
      (total, candidate) => total + candidate.grossAmount,
      0,
    ),
    potentialExpenseTotal,
    confirmedExpenseTotal,
    potentialNonPatientAllocatedTotal: potentialCandidates.reduce(
      (total, candidate) => total + candidate.nonPatientAllocatedAmount,
      0,
    ),
    confirmedNonPatientAllocatedTotal: confirmedCandidates.reduce(
      (total, candidate) => total + candidate.nonPatientAllocatedAmount,
      0,
    ),
    threshold,
    potentialDeduction:
      threshold == null || !itemizationKnown || !deductionEligible
        ? threshold == null
          ? null
          : itemizationKnown
            ? 0
            : null
        : Math.max(potentialExpenseTotal - threshold, 0),
    confirmedDeduction:
      threshold == null || !itemizationKnown || !deductionEligible
        ? threshold == null
          ? null
          : itemizationKnown
            ? 0
            : null
        : Math.max(confirmedExpenseTotal - threshold, 0),
  };
}
